/**
 * Monogram Selector
 * Populates the letter <select> and style pill links for monogram products.
 *
 * Portability: drop this file into any Shopify store that uses the
 * monogram-selector.liquid snippet + monogram-selector.css asset.
 * To add a new style, add one entry to the STYLES array below.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Letter definitions
  // Order matters: multi-character digraphs must come before their first letter
  // so handle-parsing tries them first (longest-match strategy).
  // ---------------------------------------------------------------------------
  const LETTERS = [
    { display: 'A',  handle: 'a'  },
    { display: 'Á',  handle: 'a', special: true },
    { display: 'B',  handle: 'b'  },
    { display: 'C',  handle: 'c'  },
    { display: 'CS', handle: 'cs' },
    { display: 'D',  handle: 'd'  },
    { display: 'E',  handle: 'e'  },
    { display: 'É',  handle: 'e', special: true },
    { display: 'F',  handle: 'f'  },
    { display: 'G',  handle: 'g'  },
    { display: 'GY', handle: 'gy' },
    { display: 'H',  handle: 'h'  },
    { display: 'I',  handle: 'i'  },
    { display: 'J',  handle: 'j'  },
    { display: 'K',  handle: 'k'  },
    { display: 'L',  handle: 'l'  },
    { display: 'M',  handle: 'm'  },
    { display: 'N',  handle: 'n'  },
    { display: 'NY', handle: 'ny' },
    { display: 'O',  handle: 'o'  },
    { display: 'Ö',  handle: 'o', special: true },
    { display: 'P',  handle: 'p'  },
    { display: 'Q',  handle: 'q'  },
    { display: 'R',  handle: 'r'  },
    { display: 'S',  handle: 's'  },
    { display: 'SZ', handle: 'sz' },
    { display: 'T',  handle: 't'  },
    { display: 'U',  handle: 'u'  },
    { display: 'Ü',  handle: 'u', special: true },
    { display: 'V',  handle: 'v'  },
    { display: 'W',  handle: 'w'  },
    { display: 'X',  handle: 'x'  },
    { display: 'Y',  handle: 'y'  },
    { display: 'Z',  handle: 'z'  },
    { display: 'ZS', handle: 'zs' },
  ];

  // ---------------------------------------------------------------------------
  // Style definitions
  //
  // Each entry:
  //   tag      — Shopify product tag that identifies this style
  //   name     — display label shown in the style pill
  //   strategy — how to build / parse handles:
  //     'heartcoded'  => {letter}-{keyword}-{mobile-case}
  //                      special: {letter}-{keyword}-ii-{mobile-case}
  //     'mytype'      => {letter}-{keyword}-{mobile-case}
  //                      special: {letter}-ii-{keyword}-{mobile-case}
  //     'poetink'     => {prefix}-{letter}-{mobile-case}
  //                      special: {prefix}-{letter}-ii-{mobile-case}
  //   keyword  — middle segment (heartcoded / mytype strategies)
  //   prefix   — leading segment (poetink strategy)
  // ---------------------------------------------------------------------------
  const STYLES = [
    {
      tag:      'HEARTCODED B',
      name:     'BLACK 2.0',
      strategy: 'heartcoded',
      keyword:  'black',
    },
    {
      tag:      'HEARTCODED BU',
      name:     'Burgundia 2.0',
      strategy: 'heartcoded',
      keyword:  'burgundia',
    },
    {
      tag:      'MY TYPE OS',
      name:     '(Off Space) 2.0',
      strategy: 'mytype',
      keyword:  'off-space',
    },
    {
      tag:      'MY TYPE S',
      name:     '(Sorbet) 2.0',
      strategy: 'mytype',
      keyword:  'sorbet',
    },
    {
      tag:      'POET INK JP',
      name:     '(Jellyfish Presso) 2.0',
      strategy: 'poetink',
      prefix:   'jellyfish-presso',
    },
    {
      tag:      'POET INK DB',
      name:     '(Dark Blush) 2.0',
      strategy: 'poetink',
      prefix:   'dark-blush',
    },
    {
      tag:      'POET INK OS',
      name:     '(Off Space) 2.0',
      strategy: 'poetink',
      prefix:   'off-space',
    },
    {
      tag:      'POET INK S',
      name:     '(Sorbet) 2.0',
      strategy: 'poetink',
      prefix:   'sorbet',
    },
  ];

  // ---------------------------------------------------------------------------
  // Handle building
  // ---------------------------------------------------------------------------

  /**
   * Build a product handle for a given style + letter + mobile-case suffix.
   * @param {Object} style  — entry from STYLES
   * @param {Object} letter — entry from LETTERS
   * @param {string} mobileCase — the mobile-case part extracted from the current handle
   * @returns {string}
   */
  function buildHandle(style, letter, mobileCase) {
    const isSpecial = letter.special === true;
    const lh = letter.handle; // base latin handle character(s)

    switch (style.strategy) {
      case 'heartcoded':
        // Normal:  {letter}-{keyword}-{mobile-case}
        // Special: {letter}-{keyword}-ii-{mobile-case}
        if (isSpecial) {
          return `${lh}-${style.keyword}-ii-${mobileCase}`;
        }
        return `${lh}-${style.keyword}-${mobileCase}`;

      case 'mytype':
        // Normal:  {letter}-{keyword}-{mobile-case}
        // Special: {letter}-ii-{keyword}-{mobile-case}
        if (isSpecial) {
          return `${lh}-ii-${style.keyword}-${mobileCase}`;
        }
        return `${lh}-${style.keyword}-${mobileCase}`;

      case 'poetink':
        // Normal:  {prefix}-{letter}-{mobile-case}
        // Special: {prefix}-{letter}-ii-{mobile-case}
        if (isSpecial) {
          return `${style.prefix}-${lh}-ii-${mobileCase}`;
        }
        return `${style.prefix}-${lh}-${mobileCase}`;

      default:
        return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Handle parsing — extract current letter + mobile-case from a product handle
  // ---------------------------------------------------------------------------

  /**
   * Parse a product handle and return { letter, mobileCase } for the given style.
   * Returns null if the handle does not match the style's expected pattern.
   *
   * @param {string} handle
   * @param {Object} style
   * @returns {{ letter: Object, mobileCase: string } | null}
   */
  function parseHandle(handle, style) {
    switch (style.strategy) {
      case 'heartcoded':
        return parseHeartcoded(handle, style.keyword);
      case 'mytype':
        return parseMytype(handle, style.keyword);
      case 'poetink':
        return parsePoetink(handle, style.prefix);
      default:
        return null;
    }
  }

  /**
   * HEARTCODED:  {letter}-{keyword}-{mobile-case}
   *              {letter}-{keyword}-ii-{mobile-case}   (special Á/É/Ö/Ü)
   */
  function parseHeartcoded(handle, keyword) {
    const sep = `-${keyword}-`;
    const idx = handle.indexOf(sep);
    if (idx === -1) return null;

    const letterPart = handle.slice(0, idx);            // e.g. "a", "b", "sz"
    const rest       = handle.slice(idx + sep.length);  // e.g. "iphone-12" or "ii-iphone-12"

    let mobileCase, isSpecial;
    if (rest.startsWith('ii-')) {
      mobileCase = rest.slice(3);
      isSpecial  = true;
    } else {
      mobileCase = rest;
      isSpecial  = false;
    }

    const letter = findLetter(letterPart, isSpecial);
    if (!letter) return null;
    return { letter, mobileCase };
  }

  /**
   * MY TYPE:  {letter}-{keyword}-{mobile-case}
   *           {letter}-ii-{keyword}-{mobile-case}   (special Á/É/Ö/Ü)
   *
   * The keyword may contain hyphens (e.g. "off-space"), so we scan right-to-left:
   * try to match -{keyword}- working backwards through the handle.
   */
  function parseMytype(handle, keyword) {
    const sep = `-${keyword}-`;
    const idx = handle.indexOf(sep);
    if (idx === -1) return null;

    const leftPart = handle.slice(0, idx);  // e.g. "a", "a-ii"
    const mobileCase = handle.slice(idx + sep.length);

    let letterHandle, isSpecial;
    if (leftPart.endsWith('-ii')) {
      letterHandle = leftPart.slice(0, -3);
      isSpecial    = true;
    } else {
      letterHandle = leftPart;
      isSpecial    = false;
    }

    const letter = findLetter(letterHandle, isSpecial);
    if (!letter) return null;
    return { letter, mobileCase };
  }

  /**
   * POET INK:  {prefix}-{letter}-{mobile-case}
   *            {prefix}-{letter}-ii-{mobile-case}   (special Á/É/Ö/Ü)
   *
   * Remove the prefix, then match letter identifiers longest-first.
   */
  function parsePoetink(handle, prefix) {
    const prefixSep = `${prefix}-`;
    if (!handle.startsWith(prefixSep)) return null;

    const afterPrefix = handle.slice(prefixSep.length); // e.g. "a-iphone-12" or "a-ii-iphone-12"

    // Try to match each letter handle, longest first, then check for special "-ii-"
    // Build candidates: multi-char digraphs first, then single chars
    const candidates = LETTERS.slice().sort((a, b) => b.handle.length - a.handle.length);

    for (const letter of candidates) {
      const lh = letter.handle;

      // Special form: {lh}-ii-{mobile-case}
      if (afterPrefix.startsWith(`${lh}-ii-`)) {
        // This letter must have a special variant
        const specialLetter = LETTERS.find(l => l.handle === lh && l.special === true);
        if (specialLetter) {
          const mobileCase = afterPrefix.slice(`${lh}-ii-`.length);
          return { letter: specialLetter, mobileCase };
        }
      }

      // Normal form: {lh}-{mobile-case}
      if (afterPrefix.startsWith(`${lh}-`)) {
        // Since we iterate longest-first, a digraph is tried before single,
        // so prefix collision can't happen.
        const normalLetter = LETTERS.find(l => l.handle === lh && !l.special);
        if (normalLetter) {
          const mobileCase = afterPrefix.slice(`${lh}-`.length);
          return { letter: normalLetter, mobileCase };
        }
      }
    }

    return null;
  }

  /**
   * Find the Letter object for a given handle string and special flag.
   * @param {string} handle
   * @param {boolean} isSpecial
   * @returns {Object|null}
   */
  function findLetter(handle, isSpecial) {
    return LETTERS.find(l => l.handle === handle && (l.special === true) === isSpecial) || null;
  }

  // ---------------------------------------------------------------------------
  // Swiper initialisation for the style-row slider
  // ---------------------------------------------------------------------------

  const BREAKPOINT = 750;
  const SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css';
  const SWIPER_JS  = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';

  function loadSwiper() {
    if (window.__ccSwiperPromise) return window.__ccSwiperPromise;

    window.__ccSwiperPromise = new Promise(function (resolve, reject) {
      if (window.Swiper) { resolve(window.Swiper); return; }

      if (!document.querySelector('link[href="' + SWIPER_CSS + '"]')) {
        const link = document.createElement('link');
        link.rel  = 'stylesheet';
        link.href = SWIPER_CSS;
        document.head.appendChild(link);
      }

      const existing = document.querySelector('script[src="' + SWIPER_JS + '"]');
      if (existing) {
        existing.addEventListener('load',  function () { resolve(window.Swiper); });
        existing.addEventListener('error', reject);
        return;
      }

      const s = document.createElement('script');
      s.src = SWIPER_JS;
      s.addEventListener('load',  function () { resolve(window.Swiper); });
      s.addEventListener('error', reject);
      document.head.appendChild(s);
    });

    return window.__ccSwiperPromise;
  }

  function initStyleSlider(container) {
    const sliderRoot = container.querySelector('.monogram-selector__slider-root');
    if (!sliderRoot) return;

    const ds = container.dataset;
    const itemsMobile      = parseFloat(ds.msItemsMobile)      || 2;
    const itemsDesktop     = parseFloat(ds.msItemsDesktop)     || 4;
    const loop             = ds.msLoop             === 'true';
    const autoplayEnabled  = ds.msAutoplay         === 'true';
    const autoplaySpeed    = parseFloat(ds.msAutoplaySpeed)    || 4000;
    const showDots         = ds.msDots             === 'true';
    const desktopArrows    = ds.msDesktopArrows    === 'true';
    const mobileArrows     = ds.msMobileArrows     === 'true';
    const desktopDraggable = ds.msDesktopDraggable === 'true';
    const mobileDraggable  = ds.msMobileDraggable  === 'true';
    const gapMobile        = parseFloat(ds.msGapMobile)        || 8;
    const gapDesktop       = parseFloat(ds.msGapDesktop)       || 8;
    const transitionSpeed  = parseFloat(ds.msSpeed)            || 600;

    const swiperEl = sliderRoot.querySelector('.custom-carousel__swiper');
    const prevBtn  = sliderRoot.querySelector('.custom-carousel__prev');
    const nextBtn  = sliderRoot.querySelector('.custom-carousel__next');
    if (!swiperEl) return;

    loadSwiper().then(function () {
      const isDesktop = window.matchMedia('(min-width: ' + BREAKPOINT + 'px)').matches;

      const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
      const initialSlide = Math.max(0, slides.findIndex(function (s) {
        return s.querySelector('.monogram-selector__style-pill.is-active');
      }));

      const swiper = new window.Swiper(swiperEl, {
        speed:                    transitionSpeed,
        centeredSlides:           loop,
        centerInsufficientSlides: true,
        snapToSlideEdge:          loop,
        roundLengths:             true,
        observer:                 true,
        observeParents:           true,
        loop:                     loop,
        initialSlide:             initialSlide,
        slidesPerView:            itemsMobile,
        spaceBetween:             gapMobile,
        allowTouchMove:           isDesktop ? desktopDraggable : mobileDraggable,
        breakpoints: {
          [BREAKPOINT]: {
            centeredSlides:  loop,
            slidesPerView:   itemsDesktop,
            spaceBetween:    gapDesktop,
            allowTouchMove:  desktopDraggable,
          },
        },
        navigation: {
          prevEl: prevBtn,
          nextEl: nextBtn,
          disabledClass: 'custom-carousel__nav--disabled',
        },
        pagination: showDots
          ? { el: sliderRoot.querySelector('.swiper-pagination'), clickable: true }
          : false,
        autoplay: autoplayEnabled
          ? { delay: autoplaySpeed, disableOnInteraction: false, pauseOnMouseEnter: true }
          : false,
      });

      function setArrows(visible) {
        if (prevBtn) prevBtn.hidden = !visible;
        if (nextBtn) nextBtn.hidden = !visible;
      }
      setArrows(isDesktop ? desktopArrows : mobileArrows);

      const mq = window.matchMedia('(min-width: ' + BREAKPOINT + 'px)');
      mq.addEventListener('change', function (e) {
        setArrows(e.matches ? desktopArrows : mobileArrows);
        swiper.params.allowTouchMove = e.matches ? desktopDraggable : mobileDraggable;
        swiper.update();
      });
    }).catch(function (err) {
      console.error('[monogram-selector] Failed to load Swiper:', err);
    });
  }

  // ---------------------------------------------------------------------------

  function init(container) {
    const productHandle = container.dataset.productHandle;
    const productTags   = (container.dataset.productTags || '').split(',').map(t => t.trim());

    // Identify current style
    const currentStyle = STYLES.find(s => productTags.includes(s.tag));
    if (!currentStyle) return; // no matching tag — nothing to render

    // Parse the current handle to get letter + mobile-case
    const parsed = parseHandle(productHandle, currentStyle);
    if (!parsed) return; // handle doesn't match expected pattern

    const { letter: currentLetter, mobileCase } = parsed;

    buildLetterSelect(container, currentStyle, currentLetter, mobileCase);
    buildStylePills(container, currentStyle, currentLetter, mobileCase);
  }

  function buildLetterSelect(container, currentStyle, currentLetter, mobileCase) {
    const select = document.querySelector('.monogram-selector__letter-select');
    if (!select) return;

    // Remove the placeholder <option> added by Liquid
    select.innerHTML = '';

    LETTERS.forEach(function (letter) {
      const handle = buildHandle(currentStyle, letter, mobileCase);
      const option = document.createElement('option');
      option.value = `/products/${handle}`;
      option.textContent = letter.display;

      const isCurrent = letter.display === currentLetter.display;
      if (isCurrent) option.selected = true;

      select.appendChild(option);
    });

    select.addEventListener('change', function () {
      if (this.value) {
        window.location.href = this.value;
      }
    });
  }

  /**
   * Insert a Shopify size modifier into a CDN image URL.
   * e.g. '//cdn.shopify.com/…/img.jpg?v=1' → '//cdn.shopify.com/…/img_80x80_crop_center.jpg?v=1'
   * @param {string} src
   * @param {string} size — e.g. '80x80_crop_center'
   * @returns {string}
   */
  function shopifyImageSize(src, size) {
    return src.replace(/(\.[a-zA-Z]+)(\?|$)/, `_${size}$1$2`);
  }

  function buildStylePills(container, currentStyle, currentLetter, mobileCase) {
    const row = container.querySelector('.monogram-selector__style-row');
    if (!row) return;

    const appendTarget = row.querySelector('.swiper-wrapper');
    if (!appendTarget) return;

    const labelsEl  = container.querySelector('script[data-ms-labels]');
    const labelsMap = labelsEl ? JSON.parse(labelsEl.textContent) : {};

    STYLES.forEach(function (style) {
      const handle = buildHandle(style, currentLetter, mobileCase);
      const isActive = style.tag === currentStyle.tag;

      // Build pill
      const a = document.createElement('a');
      a.href = `/products/${handle}`;
      a.className = 'monogram-selector__style-pill';
      if (isActive) {
        a.classList.add('is-active');
        a.setAttribute('aria-current', 'page');
      }

      const labelText = labelsMap[style.tag];
      if (labelText) {
        const nameSpan = document.createElement('span');
        nameSpan.className = 'monogram-selector__pill-name';
        nameSpan.textContent = labelText;
        a.appendChild(nameSpan);
      }

      // Image wrapper — shows spinner until fetch resolves
      const imgWrap = document.createElement('span');
      imgWrap.className = 'monogram-selector__pill-img-wrap';

      const spinner = document.createElement('span');
      spinner.className = 'monogram-selector__pill-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      imgWrap.appendChild(spinner);

      a.appendChild(imgWrap);

      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(a);
      appendTarget.appendChild(slide);

      // Fetch featured image in parallel — non-blocking
      fetch(`/products/${handle}.js`)
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          if (!data || !data.featured_image) {
            imgWrap.removeChild(spinner);
            return;
          }
          const img = document.createElement('img');
          img.src = shopifyImageSize(data.featured_image, 'large');
          img.alt = style.name;
          img.className = 'monogram-selector__pill-img';
          img.width = 40;
          img.height = 40;
          imgWrap.replaceChild(img, spinner);
        })
        .catch(function () {
          // Fetch failed — silently remove the spinner
          if (spinner.parentNode === imgWrap) imgWrap.removeChild(spinner);
        });
    });

    initStyleSlider(container);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function boot() {
    document.querySelectorAll('.monogram-selector').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
